import React, { useState } from "react";
import {
  Check,
  Phone,
  PencilRuler,
  Code2,
  Rocket,
  Plus,
} from "lucide-react";

const STEPS = [
  {
    k: "01",
    title: "Discovery call",
    desc: "A free 30-minute call where we learn your business, goals, and what a great site looks like for you.",
    Icon: Phone,
  },
  {
    k: "02",
    title: "Design",
    desc: "Custom layouts drafted from scratch — you review, we refine, until it feels unmistakably yours.",
    Icon: PencilRuler,
  },
  {
    k: "03",
    title: "Build",
    desc: "Clean, hand-written code. Fast, responsive, accessible. Zero bloat, zero throwaway plugins.",
    Icon: Code2,
  },
  {
    k: "04",
    title: "Launch & support",
    desc: "We ship it live, hand over the keys, and stick around for two weeks of free post-launch tweaks.",
    Icon: Rocket,
  },
];

const INCLUDES = [
  "Custom design — no templates",
  "Mobile & desktop responsive",
  "SEO-ready structure & metadata",
  "Analytics setup from day one",
  "Fast, reliable hosting",
  "Two weeks post-launch support",
  "One flat price, no surprises",
  "Full ownership of code & content",
];

const FAQ = [
  {
    q: "How long does it take?",
    a: "Fast. Most projects ship in days, not months — we move from kickoff to launch without the typical agency drag.",
  },
  {
    q: "How much does it cost?",
    a: "One flat project price. We'll quote it on the discovery call once we understand the scope.",
  },
  {
    q: "What do I need to provide?",
    a: "Just your brand direction and rough content. We'll guide you through everything else.",
  },
  {
    q: "Can you update it later?",
    a: "Yes. You own the site and code. We're happy to keep helping, or hand it off cleanly.",
  },
];

export default function Details() {
  return (
    <section
      id="details"
      data-testid="details-section"
      className="relative py-16 sm:py-24 lg:py-32 border-t border-[rgba(10,10,10,0.08)]"
    >
      <div className="section-container">
        <div className="mx-auto max-w-[980px]">
          <div
            className="reveal tick-label"
            data-testid="details-eyebrow"
          >
            More detail
          </div>
          <h2
            className="reveal font-sans-display mt-4 sm:mt-5 text-[30px] sm:text-[44px] lg:text-[56px] font-bold leading-[1.08] sm:leading-[1.05] tracking-[-0.03em] text-[#0a0a0a]"
            data-testid="details-heading"
          >
            How it{" "}
            <span className="font-display italic font-normal">works</span>, end
            to end.
          </h2>
          <p
            className="reveal reveal-delay-1 mt-4 sm:mt-5 text-[15.5px] sm:text-[18px] leading-[1.6] text-[#0a0a0a]/70 max-w-[620px]"
            data-testid="details-paragraph"
          >
            A calm, predictable process — no agency jargon, no surprise
            invoices. Here's exactly how we'll take your idea and turn it into
            a website you're proud to send people to.
          </p>

          {/* Process */}
          <div className="reveal reveal-delay-2 mt-12 sm:mt-16 relative">
            <div className="tick-label mb-5 sm:mb-6">The process</div>

            <div className="relative">
              {/* dashed connector line — desktop only */}
              <div className="hidden lg:block step-connector" aria-hidden />

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5 relative">
                {STEPS.map((s, i) => (
                  <Step key={s.k} {...s} i={i} />
                ))}
              </div>
            </div>
          </div>

          {/* What's included */}
          <div className="reveal mt-14 sm:mt-20 grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12 items-start">
            <div className="lg:col-span-2">
              <div className="tick-label mb-3">What you get</div>
              <h3 className="font-sans-display text-[24px] sm:text-[30px] font-bold leading-[1.15] tracking-[-0.02em] text-[#0a0a0a]">
                Every site ships with{" "}
                <span className="font-display italic font-normal">
                  everything
                </span>{" "}
                it needs.
              </h3>
              <p className="mt-3 text-[14.5px] sm:text-[15px] leading-[1.6] text-[#0a0a0a]/65">
                No upsells, no "premium" tiers. One price, fully included.
              </p>
            </div>
            <ul
              className="lg:col-span-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3"
              data-testid="details-includes-list"
            >
              {INCLUDES.map((item) => (
                <li
                  key={item}
                  className="flex items-start gap-3 text-[14.5px] sm:text-[15px] text-[#0a0a0a]/85"
                >
                  <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#0a0a0a] text-[#fbf9f4] shadow-[1.5px_1.5px_0_0_rgba(10,10,10,0.15)]">
                    <Check className="size-3" strokeWidth={3} />
                  </span>
                  {item}
                </li>
              ))}
            </ul>
          </div>

          {/* FAQ — interactive accordion */}
          <div className="reveal mt-14 sm:mt-20">
            <div className="tick-label mb-5 sm:mb-6">Common questions</div>
            <div
              className="rounded-2xl border border-[rgba(10,10,10,0.1)] bg-white overflow-hidden divide-y divide-[rgba(10,10,10,0.08)] shadow-[0_20px_50px_-30px_rgba(10,10,10,0.18)]"
              data-testid="details-faq-list"
            >
              {FAQ.map((f, i) => (
                <FaqItem key={f.q} q={f.q} a={f.a} defaultOpen={i === 0} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Step({ k, title, desc, Icon, i }) {
  return (
    <div
      className="group relative rounded-2xl border border-[rgba(10,10,10,0.1)] bg-white p-5 sm:p-6 card-lift"
      data-testid={`details-step-${k}`}
      style={{ transitionDelay: `${i * 60}ms` }}
    >
      <div className="flex items-center justify-between">
        <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-[#0a0a0a] text-[#fbf9f4] shadow-[2px_2px_0_0_rgba(10,10,10,0.15)]">
          <Icon className="size-4" strokeWidth={2} />
        </span>
        <span className="font-mono-mark text-[11.5px] text-[#0a0a0a]/45">
          STEP {k}
        </span>
      </div>
      <div className="mt-5 text-[15.5px] sm:text-[16px] font-semibold tracking-tight text-[#0a0a0a]">
        {title}
      </div>
      <div className="mt-2 text-[13.5px] sm:text-[14px] leading-[1.55] text-[#0a0a0a]/65">
        {desc}
      </div>
    </div>
  );
}

function FaqItem({ q, a, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div data-testid={`details-faq-${q.slice(0, 10)}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-start justify-between gap-6 text-left px-5 sm:px-6 py-5 sm:py-6 hover:bg-[rgba(10,10,10,0.02)] transition-colors duration-150"
        aria-expanded={open}
      >
        <span className="text-[15px] sm:text-[16px] font-semibold tracking-tight text-[#0a0a0a]">
          {q}
        </span>
        <span
          className={`inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-[rgba(10,10,10,0.15)] text-[#0a0a0a] transition-transform duration-300 ${
            open ? "rotate-45 bg-[#0a0a0a] text-[#fbf9f4] border-[#0a0a0a]" : ""
          }`}
          aria-hidden
        >
          <Plus className="size-3.5" strokeWidth={2.5} />
        </span>
      </button>
      <div
        className="grid transition-all duration-300 ease-out"
        style={{
          gridTemplateRows: open ? "1fr" : "0fr",
          opacity: open ? 1 : 0,
        }}
      >
        <div className="overflow-hidden">
          <div className="px-5 sm:px-6 pb-5 sm:pb-6 text-[13.5px] sm:text-[14.5px] leading-[1.65] text-[#0a0a0a]/65 max-w-[640px]">
            {a}
          </div>
        </div>
      </div>
    </div>
  );
}
